<ng-container *ngIf="!isEmpty; else emptyState">

    <section class="queue-section" *ngIf="kind === 'query' && queryCards.length > 0">
        <header class="section-header">
            <h3 class="section-title">Needs your review</h3>
            <span class="section-count">{{ queryCards.length }}</span>
            <span class="section-hint">Captured from thumbs-ups. The nightly gate auto-promotes these once they re-run clean — Approve to fast-track, Reject to retire.</span>
        </header>

        <div class="cards">
            <div class="review-card query-card"
                 *ngFor="let card of queryCards; trackBy: trackByQueryId"
                 [class.is-busy]="card.busy">
                <div class="query-head">
                    <span class="query-title">{{ card.query.title }}</span>
                    <app-simple-badge *ngIf="card.query.embedStatus === 'pending'"
                                      label="embed pending"
                                      color="warning"
                                      size="sm"></app-simple-badge>
                </div>

                <div class="query-pattern">
                    <span class="field-label">Question pattern</span>
                    <p class="query-pattern-text">{{ card.query.questionPattern }}</p>
                </div>

                <div class="query-sql">
                    <span class="field-label">Verified SQL</span>
                    <pre class="sample-sql"><code>{{ card.query.verifiedSql }}</code></pre>
                </div>

                <div class="query-notes" *ngIf="card.query.notes">
                    <span class="field-label">Notes</span>
                    <p class="query-notes-text">{{ card.query.notes }}</p>
                </div>

                <div class="card-actions">
                    <button type="button" class="review-btn review-btn-secondary"
                            [disabled]="card.busy" (click)="rejectQuery(card)">
                        <i class="ph-bold ph-x-circle icon-error icon-16" aria-hidden="true"></i>
                        <span>Reject</span>
                    </button>
                    <flex-spacer></flex-spacer>
                    <button type="button" class="review-btn review-btn-primary"
                            [disabled]="card.busy" (click)="approveQuery(card)">
                        <i class="ph-bold ph-check-circle icon-16" aria-hidden="true"></i>
                        <span>Approve</span>
                    </button>
                </div>
            </div>
        </div>
    </section>

    <section class="queue-section" *ngIf="kind === 'skill' && draftCards.length > 0">
        <header class="section-header">
            <h3 class="section-title">Proposed by Gable</h3>
            <span class="section-count">{{ draftCards.length }}</span>
            <span class="section-hint">Playbooks Gable repeated often enough to propose as a reusable skill — review before they join the library.</span>
        </header>

        <div class="cards">
            <div class="review-card skill-card"
                 *ngFor="let card of draftCards; trackBy: trackByDraftId"
                 [class.is-busy]="card.busy">
                <div class="query-head">
                    <span class="query-title code-style">{{ card.draft.proposedPayload?.name }}</span>
                    <app-simple-badge [label]="'Seen ' + sightingsCount(card.draft) + (sightingsCount(card.draft) === 1 ? ' time' : ' times')"
                                      color="muted"
                                      size="sm"></app-simple-badge>
                </div>

                <div class="query-notes" *ngIf="card.draft.proposedPayload?.description">
                    <p class="query-notes-text">{{ card.draft.proposedPayload?.description }}</p>
                </div>

                <div class="evidence-detail">
                    <button type="button" class="evidence-toggle" (click)="toggleEvidence(card)"
                            [attr.aria-expanded]="card.evidenceExpanded">
                        <i class="ph-bold icon-muted icon-14"
                           [ngClass]="card.evidenceExpanded ? 'ph-caret-down' : 'ph-caret-right'"
                           aria-hidden="true"></i>
                        {{ card.evidenceExpanded ? 'Hide playbook' : 'Show playbook' }}
                    </button>

                    <div class="query-sql" *ngIf="card.evidenceExpanded">
                        <span class="field-label">Playbook</span>
                        <pre class="sample-sql"><code>{{ card.draft.proposedPayload?.body }}</code></pre>
                    </div>
                </div>

                <div class="card-actions">
                    <button type="button" class="review-btn review-btn-secondary"
                            [disabled]="card.busy" (click)="rejectDraft(card)">
                        <i class="ph-bold ph-x-circle icon-error icon-16" aria-hidden="true"></i>
                        <span>Reject</span>
                    </button>
                    <flex-spacer></flex-spacer>
                    <button type="button" class="review-btn review-btn-primary"
                            [disabled]="card.busy" (click)="approveDraft(card)">
                        <i class="ph-bold ph-check-circle icon-16" aria-hidden="true"></i>
                        <span>Approve</span>
                    </button>
                </div>
            </div>
        </div>
    </section>

    <section class="queue-section" *ngIf="kind === 'lesson' && draftCards.length > 0">
        <header class="section-header">
            <h3 class="section-title">Proposed lessons</h3>
            <span class="section-count">{{ draftCards.length }}</span>
            <span class="section-hint">Write the guidance fix before approving.</span>
        </header>
        <div class="cards">
            <div class="review-card"
                 *ngFor="let card of draftCards; trackBy: trackByDraftId"
                 [class.is-busy]="card.busy">
                <!-- Evidence header -->
                <div class="card-evidence">
                    <span class="signal-badge" [ngClass]="'signal-badge--' + signalColor(card.draft)">
                        <i class="ph-bold {{ signalIcon(card.draft) }} icon-16" aria-hidden="true"></i>
                        {{ signalLabel(card.draft) }}
                    </span>

                    <div class="evidence-stats" *ngIf="card.draft.evidence as ev">
                        <app-simple-badge *ngIf="ev.clusterSize"
                                          [label]="ev.clusterSize + ' in cluster'"
                                          color="muted"
                                          size="sm"></app-simple-badge>
                        <app-simple-badge *ngIf="ev.weightedScore != null"
                                          [label]="'score ' + (ev.weightedScore | number: '1.0-2')"
                                          color="muted"
                                          size="sm"></app-simple-badge>
                        <app-simple-badge *ngFor="let chip of ev.chips; trackBy: trackByIndex"
                                          [label]="chip"
                                          color="outline"
                                          size="sm"></app-simple-badge>
                    </div>
                </div>

                <!-- Collapsible evidence samples -->
                <div class="evidence-detail" *ngIf="hasEvidenceSamples(card.draft)">
                    <button type="button" class="evidence-toggle" (click)="toggleEvidence(card)"
                            [attr.aria-expanded]="card.evidenceExpanded">
                        <i class="ph-bold icon-muted icon-14"
                           [ngClass]="card.evidenceExpanded ? 'ph-caret-down' : 'ph-caret-right'"
                           aria-hidden="true"></i>
                        {{ card.evidenceExpanded ? 'Hide evidence' : 'Show evidence' }}
                    </button>

                    <div class="evidence-samples" *ngIf="card.evidenceExpanded">
                        <div class="sample-block"
                             *ngIf="card.draft.evidence?.sampleQuestions?.length">
                            <span class="sample-label">Sample questions</span>
                            <ul class="sample-list">
                                <li *ngFor="let q of card.draft.evidence?.sampleQuestions; trackBy: trackByIndex">
                                    {{ q }}
                                </li>
                            </ul>
                        </div>
                        <div class="sample-block"
                             *ngIf="card.draft.evidence?.sampleSql?.length">
                            <span class="sample-label">Sample SQL</span>
                            <pre class="sample-sql"
                                 *ngFor="let sql of card.draft.evidence?.sampleSql; trackBy: trackByIndex"><code>{{ sql }}</code></pre>
                        </div>
                    </div>
                </div>

                <!--
                    Editable proposed lesson. Uses the canonical V2
                    labeled-field pattern from create-draft-budget-dialog
                    (form-group wrapper, label above outlined field).
                    See CLAUDE.md › V2 Reference Patterns.
                -->
                <form class="card-form" [formGroup]="card.form">
                    <div class="form-group">
                        <label>Title</label>
                        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-100">
                            <input matInput type="text" placeholder="Lesson title" formControlName="title">
                        </mat-form-field>
                    </div>

                    <div class="form-group">
                        <label>Trigger context</label>
                        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-100">
                            <textarea matInput rows="2"
                                      placeholder="When should Gable apply this lesson?"
                                      formControlName="triggerContext"></textarea>
                        </mat-form-field>
                    </div>

                    <div class="form-group">
                        <label>
                            Guidance
                            <span class="required-hint">required before approving</span>
                        </label>
                        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-100">
                            <textarea matInput rows="4"
                                      placeholder="Write the fix — what Gable should do differently"
                                      formControlName="guidance"></textarea>
                        </mat-form-field>
                    </div>
                </form>

                <!-- Actions -->
                <div class="card-actions">
                    <button type="button" class="review-btn review-btn-secondary"
                            [disabled]="card.busy" (click)="rejectDraft(card)">
                        <i class="ph-bold ph-x-circle icon-error icon-16" aria-hidden="true"></i>
                        <span>Reject</span>
                    </button>
                    <button type="button" class="review-btn review-btn-secondary"
                            [disabled]="card.busy" (click)="saveDraft(card)">
                        <i class="ph-bold ph-floppy-disk icon-muted icon-16" aria-hidden="true"></i>
                        <span>Save edits</span>
                    </button>
                    <flex-spacer></flex-spacer>
                    <button type="button" class="review-btn review-btn-primary"
                            [disabled]="!canApproveDraft(card)"
                            [matTooltip]="canApproveDraft(card) ? '' : 'Write the guidance fix first'"
                            matTooltipClass="gable-dark-tooltip"
                            (click)="approveDraft(card)">
                        <i class="ph-bold ph-check-circle icon-16" aria-hidden="true"></i>
                        <span>Approve</span>
                    </button>
                </div>
            </div>
        </div>
    </section>
</ng-container>

<ng-template #emptyState>
    <app-empty-state *ngIf="showEmptyState" [showSpinner]="loading">
        <span emptyStateTitle>{{ loading ? 'Loading…' : 'Nothing needs review' }}</span>
        <span emptyStateText *ngIf="!loading">
            When Gable proposes something new, it lands here for your sign-off.
        </span>
    </app-empty-state>
</ng-template>
